import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, ActionIcon } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { StatusPill } from '../../components/panel';
import { HttpAuthFields } from '../flows/builder/components/HttpAuthFields';
import type { JsonObject } from '../flows/builder/mapper';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The business's API connections (19 October 2026): each one's address, its live sources and its actions, with
 * "Add more" (the wizard again, on this connection), "Sign-in" (a new key reaches every source, action and flow step at
 * once) and Remove.
 */
export function ApiConnectionsList({ connections, onAddMore, onChanged }: {
  connections: flows.ApiConnection[];
  onAddMore: (c: flows.ApiConnection) => void;
  onChanged: () => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState<flows.ApiConnection | null>(null);
  const remove = useMutation({
    mutationFn: (c: flows.ApiConnection) => flows.deleteApiConnection(c.id),
    onSuccess: (_r, c) => { onChanged(); notifySuccess(t('knowledge.api.list.removed'), c.name); },
    onError: (e, c) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), c.name)
  });
  const removeAction = useMutation({
    mutationFn: (a: flows.ApiAction) => flows.deleteApiAction(a.connection_id, a.id),
    onSuccess: (_r, a) => { onChanged(); notifySuccess(t('knowledge.api.list.actionRemoved'), a.name); },
    onError: (e, a) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), a.name)
  });
  if (connections.length === 0) return null;

  return (
    <Stack gap={6} data-testid="api-connections">
      <Text role="inputLabel">{t('knowledge.api.list.title')}</Text>
      {connections.map((c) => (
        <div key={c.id} className="rr-knowledge__source" data-testid="api-connection">
          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Group gap="xs" wrap="wrap">
              <Text role="bodySmall" fw={650}>{c.name}</Text>
              <StatusPill tone={c.status === 'failing' ? 'rejected' : c.status === 'ok' ? 'active' : 'submitted'}>{t(`knowledge.api.status.${c.status}`)}</StatusPill>
            </Group>
            <Text role="caption" c={MUTED} style={{ overflowWrap: 'anywhere' }}>
              {c.base_url} · {t('knowledge.api.list.counts', { sources: c.sources.length, actions: c.actions.length })}
            </Text>
            {c.status === 'failing' && c.last_error && <Text role="caption" c="var(--rr-color-error, #dc2626)">{c.last_error}</Text>}
            {c.actions.length > 0 && (
              <details>
                <summary style={{ fontSize: 13 }}>{t('knowledge.api.list.actions', { count: c.actions.length })}</summary>
                <Stack gap={2}>
                  {c.actions.map((a) => (
                    <Group key={a.id} gap="xs" wrap="nowrap" justify="space-between">
                      <Text role="caption"><strong>{a.name}</strong> <code>{a.method} {a.path}</code></Text>
                      <ActionIcon variant="subtle" size="sm" color="error" aria-label={t('knowledge.api.list.removeAction', { name: a.name })}
                        onClick={() => removeAction.mutate(a)}>×</ActionIcon>
                    </Group>
                  ))}
                  <Text role="caption" c={MUTED}>{t('knowledge.api.list.actionsHint')}</Text>
                </Stack>
              </details>
            )}
          </Stack>
          <Group gap={4} wrap="nowrap">
            <Button size="xs" variant="default" onClick={() => onAddMore(c)}>{t('knowledge.api.list.addMore')}</Button>
            <Button size="xs" variant="subtle" onClick={() => setEditing(c)}>{t('knowledge.api.list.signIn')}</Button>
            <ActionIcon
              variant="subtle" size="sm" color="error"
              aria-label={t('knowledge.api.list.remove', { name: c.name })}
              onClick={() => openConfirmModal({
                title: t('knowledge.api.list.removeTitle'),
                children: <Text role="bodySmall">{t('knowledge.api.list.removeBody', { name: c.name, sources: c.sources.length, actions: c.actions.length })}</Text>,
                labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                confirmProps: { color: 'red' },
                onConfirm: () => remove.mutate(c)
              })}
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M2.8 4.2h10.4M6.2 4.2V2.8h3.6v1.4M4.2 4.2l.6 9h6.4l.6-9M6.6 6.8v4M9.4 6.8v4" />
              </svg>
            </ActionIcon>
          </Group>
        </div>
      ))}
      <ApiConnectionForm connection={editing} onClose={() => setEditing(null)} onSaved={onChanged} />
    </Stack>
  );
}

/** A connection's name, address and sign-in. A secret left empty keeps the saved one. */
function ApiConnectionForm({ connection, onClose, onSaved }: { connection: flows.ApiConnection | null; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [auth, setAuth] = useState<JsonObject>({});
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!connection) return;
    setName(connection.name); setBaseUrl(connection.base_url); setAuth({ ...(connection.auth as JsonObject) }); setError(null);
  }, [connection]);

  const save = useMutation({
    mutationFn: () => flows.updateApiConnection(connection!.id, {
      name: name.trim(), base_url: baseUrl.trim(), auth: { ...(auth.type ? auth : { type: 'none' }) } as flows.ApiConnectionInput['auth'], headers: connection!.headers
    }),
    onSuccess: (res) => { onSaved(); notifySuccess(t('knowledge.api.list.saved'), res.data.name); onClose(); },
    onError: (e) => setError(e instanceof Error ? e.message : t('common.unknownError'))
  });

  return (
    <Drawer opened={connection !== null} onClose={onClose} position="right" size="md" title={t('knowledge.api.list.signInTitle')}>
      <Stack gap="md" style={{ padding: 4 }} data-testid="api-connection-form">
        <TextInput label={t('knowledge.api.connect.name')} value={name} maxLength={120} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput label={t('knowledge.api.connect.baseUrl')} description={t('knowledge.api.connect.baseUrlHint')} value={baseUrl} onChange={(e) => setBaseUrl(e.currentTarget.value)} />
        <HttpAuthFields
          auth={auth}
          onChange={(next) => setAuth(next ?? { type: 'none' })}
          t={t}
          secretHint={t('knowledge.api.connect.secretHint')}
          saved={connection && connection.auth.type === auth.type ? connection.secret_fields : []}
          savedPlaceholder={t('knowledge.live.form.secretSaved')}
        />
        <Text role="caption" c={MUTED}>{t('knowledge.api.list.signInHint')}</Text>
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={save.isPending} disabled={!name.trim() || !baseUrl.trim()} onClick={() => { setError(null); save.mutate(); }}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
